import { CONTENT_PLATFORM_PROPERTY_NAME, contentIsPublished, contentIsPublishedWithScheduledUpdate } from 'content-management-data-lib';
import { registerQuery, useQuery } from 'data-fetching-client';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import http from 'hub-http/clients/apiClient';
import { PublishActionTypes } from './types/publishButtonTypes';
import { useSelector } from 'react-redux';
import { getIsStagingV2 } from '../../redux/selectors/authSelectors';
import { useGetStagedPageQuery } from '../../../__generated__/chirp/com/hubspot/contentstaging/rpc/ContentStagingPagesRpc.hooks';
import { buildStagingDomainUrl } from '../../lib/preview/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const ContentCrmObjectQuery = registerQuery({
  fieldName: 'contentCrmObject',
  args: ['contentObjectType', 'contentCrmObjectId'],
  fetcher: ({
    contentObjectType,
    contentCrmObjectId
  }) => {
    return http.get(`/content-management/search/v1/${contentObjectType}/${contentCrmObjectId}`);
  }
});
const makeProp = value => ({
  value,
  rawValue: value
});
const mapStagedPageToContentCrmObject = stagedPage => {
  // DRAFT: page not yet on the staging domain → RESOLVED_STATE DRAFT
  //   → publishType 'publish' → "Publish to Staging" → proofPage()
  // PROOF: page is on the staging domain → RESOLVED_STATE PUBLISHED
  //   → publishType 'update' → "Publish" / "Update"
  //   → non-scheduling: proofPage() (re-proof); scheduling: schedulePublishForStaging()
  const resolvedState = stagedPage.status === 'PROOF' ? 'PUBLISHED' : 'DRAFT';
  return {
    objectId: stagedPage.contentId,
    // objectTypeId and portalId are not currently read by any publishing component
    objectTypeId: '',
    portalId: 0,
    properties: {
      [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: makeProp(resolvedState),
      [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: makeProp(resolvedState),
      [CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT]: makeProp(null),
      // TODO: populate from ContentStagingPublishEventsRpc so scheduled state survives a hard refresh
      // https://github.com/HubSpotEngineering/pages/issues/5241
      [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: makeProp(null),
      [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: makeProp(null),
      [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: makeProp(null)
    }
  };
};
export const ContentCrmObjectContext = /*#__PURE__*/createContext(undefined);
const ContentCrmObjectProvider = ({
  contentObjectType,
  contentCrmObjectId,
  children
}) => {
  var _stagedPageData$stage, _stagedPageData$stage2, _stagedPageData$stage3, _stagedPageData$stage4, _stagedPageData$stage5, _stagedPageData$stage6, _stagedPageData$stage7;
  const isStagingV2 = useSelector(getIsStagingV2);
  const [contentCrmObject, setContentCrmObject] = useState(undefined);
  const {
    loading: crmLoading,
    error: crmError
  } = useQuery(ContentCrmObjectQuery, {
    variables: {
      contentObjectType,
      contentCrmObjectId
    },
    skip: isStagingV2,
    onCompleted: response => {
      setContentCrmObject(response.contentCrmObject);
    }
  });
  const {
    data: stagedPageData,
    loading: stagingLoading,
    error: stagingError
  } = useGetStagedPageQuery({
    variables: {
      contentId: contentCrmObjectId
    },
    skip: !isStagingV2
  });
  useEffect(() => {
    if (isStagingV2 && stagedPageData !== null && stagedPageData !== void 0 && stagedPageData.stagedPage) {
      setContentCrmObject(mapStagedPageToContentCrmObject(stagedPageData.stagedPage));
    }
  }, [isStagingV2, stagedPageData]);
  const loading = isStagingV2 ? stagingLoading : crmLoading;
  const error = isStagingV2 ? stagingError : crmError;
  const stagedPageDomainName = isStagingV2 ? stagedPageData === null || stagedPageData === void 0 || (_stagedPageData$stage = stagedPageData.stagedPage) === null || _stagedPageData$stage === void 0 ? void 0 : _stagedPageData$stage.domainName : undefined;
  const stagedPageSandboxDomain = isStagingV2 && stagedPageData !== null && stagedPageData !== void 0 && (_stagedPageData$stage2 = stagedPageData.stagedPage) !== null && _stagedPageData$stage2 !== void 0 && _stagedPageData$stage2.domainName ? new URL(buildStagingDomainUrl(stagedPageData.stagedPage.domainName, '')).hostname : undefined;
  const stagedPageStatus = isStagingV2 ? stagedPageData === null || stagedPageData === void 0 || (_stagedPageData$stage3 = stagedPageData.stagedPage) === null || _stagedPageData$stage3 === void 0 ? void 0 : _stagedPageData$stage3.status : undefined;
  const stagedPageName = isStagingV2 ? stagedPageData === null || stagedPageData === void 0 || (_stagedPageData$stage4 = stagedPageData.stagedPage) === null || _stagedPageData$stage4 === void 0 ? void 0 : _stagedPageData$stage4.name : undefined;
  const stagedPageSlug = isStagingV2 ? stagedPageData === null || stagedPageData === void 0 || (_stagedPageData$stage5 = stagedPageData.stagedPage) === null || _stagedPageData$stage5 === void 0 ? void 0 : _stagedPageData$stage5.slug : undefined;
  const stagedPageHtmlTitle = isStagingV2 ? stagedPageData === null || stagedPageData === void 0 || (_stagedPageData$stage6 = stagedPageData.stagedPage) === null || _stagedPageData$stage6 === void 0 ? void 0 : _stagedPageData$stage6.htmlTitle : undefined;
  const stagedPagePreviewUrl = isStagingV2 ? stagedPageData === null || stagedPageData === void 0 || (_stagedPageData$stage7 = stagedPageData.stagedPage) === null || _stagedPageData$stage7 === void 0 ? void 0 : _stagedPageData$stage7.previewUrl : undefined;
  const reconcileContentCrmObjectForPublishActions = useCallback((actionType, scheduledDates) => {
    if (!contentCrmObject) {
      return;
    }
    switch (actionType) {
      case PublishActionTypes.PUBLISH:
        {
          const publishedAt = Date.now();
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT]: {
                  value: `${publishedAt}`,
                  rawValue: publishedAt
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: 'PUBLISHED',
                  rawValue: 'PUBLISHED'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: {
                  value: 'PUBLISHED',
                  rawValue: 'PUBLISHED'
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.UNPUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: 'DRAFT',
                  rawValue: 'DRAFT'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: {
                  value: 'DRAFT',
                  rawValue: 'DRAFT'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.SCHEDULE_PUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            const isPublished = contentIsPublished(prevContentCrmObject);
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: isPublished ? 'SCHEDULED_UPDATE' : 'SCHEDULED',
                  rawValue: isPublished ? 'SCHEDULED_UPDATE' : 'SCHEDULED'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
                  value: `${scheduledDates.schedulePublishDate}`,
                  rawValue: scheduledDates.schedulePublishDate
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.SCHEDULE_UNPUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: `${scheduledDates.scheduleUnpublishDate}`,
                  rawValue: scheduledDates.scheduleUnpublishDate
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: scheduledDates.unpublishExpiryUrl ? {
                  value: scheduledDates.unpublishExpiryUrl,
                  rawValue: scheduledDates.unpublishExpiryUrl
                } : prevContentCrmObject.properties[CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]
              })
            });
          });
          break;
        }
      case PublishActionTypes.CANCEL_SCHEDULED_PUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            const isCancelingScheduledUpdate = contentIsPublishedWithScheduledUpdate(prevContentCrmObject);
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE]: {
                  value: isCancelingScheduledUpdate ? 'PUBLISHED' : 'DRAFT',
                  rawValue: isCancelingScheduledUpdate ? 'PUBLISHED' : 'DRAFT'
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: {
                  value: null,
                  rawValue: null
                }
              })
            });
          });
          break;
        }
      case PublishActionTypes.CANCEL_SCHEDULED_UNPUBLISH:
        {
          setContentCrmObject(prevContentCrmObject => {
            return Object.assign({}, prevContentCrmObject, {
              properties: Object.assign({}, prevContentCrmObject.properties, {
                [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE]: {
                  value: null,
                  rawValue: null
                },
                [CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL]: {
                  value: null,
                  rawValue: null
                }
              })
            });
          });
          break;
        }
      default:
        break;
    }
  }, [contentCrmObject]);
  const value = useMemo(() => ({
    contentCrmObject,
    contentCrmObjectLoading: loading,
    reconcileContentCrmObjectForPublishActions,
    contentCrmObjectId,
    contentObjectType,
    contentCrmObjectError: error,
    isStagingV2,
    stagedPageDomainName,
    stagedPageSandboxDomain,
    stagedPageStatus,
    stagedPageName,
    stagedPageSlug,
    stagedPageHtmlTitle,
    stagedPagePreviewUrl
  }), [contentCrmObject, contentCrmObjectId, contentObjectType, error, loading, reconcileContentCrmObjectForPublishActions, isStagingV2, stagedPageDomainName, stagedPageSandboxDomain, stagedPageStatus, stagedPageName, stagedPageSlug, stagedPageHtmlTitle, stagedPagePreviewUrl]);
  return /*#__PURE__*/_jsx(ContentCrmObjectContext.Provider, {
    value: value,
    children: children
  });
};
export default ContentCrmObjectProvider;